import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { filterVideos } from "@/utils/videoUtils";
import { usePageMeta } from "@/hooks/usePageMeta";

const tabs = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [activeTab, setActiveTab] = useState("all");

  usePageMeta({
    title: keyword
      ? `搜索“${keyword}”-西瓜视频免费`
      : "搜索影片-西瓜视频免费",
    description: keyword
      ? `西瓜视频免费搜索“${keyword}”的结果页面，快速查找相关电影、电视剧与综艺内容。`
      : "在西瓜视频免费搜索想看的电影、电视剧与综艺节目。",
    keywords: `西瓜视频免费,视频搜索,${keyword || "在线视频"}`,
  });

  const results = useMemo(
    () => filterVideos({ keyword, type: activeTab === "all" ? undefined : activeTab, sort: "hot" }),
    [keyword, activeTab]
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
    setActiveTab("all");
  };

  const hasKeyword = keyword.trim().length > 0;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: "搜索结果" }]} />

      <div className="mt-4">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
          搜索影片
        </h1>
        {hasKeyword && (
          <p className="mt-2 text-sm md:text-base text-foreground-600">
            关键词：<span className="font-semibold text-primary-600">{keyword}</span>
          </p>
        )}
      </div>

      <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 items-center rounded-md border border-background-300 bg-background-100 pl-3 focus-within:border-primary-400">
          <i className="ri-search-line text-foreground-400" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入片名、导演或演员，例如：长安"
            className="w-full bg-transparent px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-search-line" />
          搜索
        </button>
      </form>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm transition-colors ${
              activeTab === tab.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {!hasKeyword ? (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-eye-line text-2xl" />
          </span>
          <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
            想看点什么呢？
          </h2>
          <p className="mt-1.5 max-w-sm text-sm text-foreground-600">
            在上方输入影片名称、导演或演员，就能快速找到相关内容。
          </p>
        </div>
      ) : results.length > 0 ? (
        <>
          <p className="mt-6 text-sm text-foreground-600">
            共找到 <span className="font-semibold text-primary-600">{results.length}</span> 个结果
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-5">
            {results.map((video, index) => (
              <VideoCard key={video.id} video={video} index={index} />
            ))}
          </div>
        </>
      ) : (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-2xl" />
          </span>
          <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
            没有找到与“{keyword}”相关的内容
          </h2>
          <p className="mt-1.5 max-w-sm text-sm text-foreground-600">
            换个关键词试试，或返回首页浏览热门推荐。
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setInput("");
                setSearchParams({});
              }}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-eraser-line" />
              清空关键词
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>
        </div>
      )}

    </div>
  );
}